/**
 * Hook: Node selection, inspector panel state, and agent message routing.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { PipelineNode } from '../DAGCanvas';
import type { AddNodeType } from '../utils/nodeTypeRegistry';
import { getNodeTypeDef } from '../utils/nodeTypeRegistry';
import type { WorkspaceOperation, WorkspaceTable } from '../../../services/workspaceApi';
import type { OutputTableInfo } from '../PipelineOutputsDrawer';
import { INSPECTOR_HEIGHT_KEY } from '../../../utils/storageKeys';
import { useLayoutFrame } from '../../../context/LayoutFrameContext';
import {
  clampInspectorHeight,
  getCreationModeInspectorHeight,
  getDataTabInspectorHeight,
  getDefaultInspectorHeight,
} from '../../../layouts/viewportLayout';

export interface CanAddNodeResult {
  allowed: boolean;
  reason?: string;
}

interface UseNodeSelectionArgs {
  pipelineNodes: PipelineNode[];
  operations: WorkspaceOperation[];
  workspaceTables: WorkspaceTable[];
  workspaceMode: 'dag' | 'mapping';
  completedOutputs: OutputTableInfo[];
  onOpenOutputsDrawer: () => void;
  sessionId: string;
  /** Whether the agent is currently busy (running, streaming). */
  isBusy?: boolean;
}

// ── SessionStorage helpers for pending node persistence ──────────────

interface PendingNodeSnapshot {
  stepType: string;
  title: string;
  outputName: string;
  inputRefs: string[];
  initialSql?: string;
  creationCode: string;
  creationExploratory: boolean;
}

function pendingStorageKey(sessionId: string): string {
  return `dataloom_pending_node_${sessionId}`;
}

function savePendingSnapshot(sessionId: string, snapshot: PendingNodeSnapshot): void {
  try {
    sessionStorage.setItem(pendingStorageKey(sessionId), JSON.stringify(snapshot));
  } catch { /* quota exceeded or unavailable — ignore */ }
}

function loadPendingSnapshot(sessionId: string): PendingNodeSnapshot | null {
  try {
    const raw = sessionStorage.getItem(pendingStorageKey(sessionId));
    return raw ? (JSON.parse(raw) as PendingNodeSnapshot) : null;
  } catch {
    return null;
  }
}

function clearPendingSnapshot(sessionId: string): void {
  try {
    sessionStorage.removeItem(pendingStorageKey(sessionId));
  } catch { /* ignore */ }
}

export function useNodeSelection({
  pipelineNodes,
  operations,
  workspaceTables,
  workspaceMode,
  completedOutputs,
  onOpenOutputsDrawer,
  sessionId,
  isBusy = false,
}: UseNodeSelectionArgs) {
  const { resetFrameVisibility } = useLayoutFrame();

  // Restore pending node from sessionStorage on mount.
  // Uses state (not memo) so it can be nulled after the first consumption,
  // preventing stale data from leaking into subsequent pending nodes.
  const [restoredSnapshot, setRestoredSnapshot] = useState(() => loadPendingSnapshot(sessionId));

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    restoredSnapshot ? '__pending__' : null,
  );
  const [selectedGroupTable, setSelectedGroupTable] = useState<{
    name: string; viewName: string; status: string; ddl?: string; error?: string;
  } | null>(null);
  const [selectedSourceTable, setSelectedSourceTable] = useState<{
    name: string; rowCount: number; columnCount: number;
  } | null>(null);
  const [pendingNode, setPendingNode] = useState<PipelineNode | null>(() => {
    if (!restoredSnapshot) return null;
    const typeLabels: Record<string, string> = {
      sql_transform: 'New SQL Transform',
      python_transform: 'New Python Transform',
      fuzzy_match: 'New Fuzzy Match',
    };
    return {
      stepId: '__pending__',
      stepType: restoredSnapshot.stepType,
      title: restoredSnapshot.title || typeLabels[restoredSnapshot.stepType] || 'New Transform',
      description: 'Configuring...',
      status: 'pending',
      inputRefs: restoredSnapshot.inputRefs || [],
      _pendingOutputName: restoredSnapshot.outputName,
      _initialSql: restoredSnapshot.initialSql,
    } as PipelineNode & { _pendingOutputName?: string; _initialSql?: string };
  });
  const [askWorkspaceMessage, setAskWorkspaceMessage] = useState<string | null>(null);
  const [inspectorFullscreen, setInspectorFullscreen] = useState(false);

  // Inspector resize state — heights are clamped to the visible workspace (see viewportLayout.ts).
  const INSPECTOR_DEFAULT_HEIGHT = getDefaultInspectorHeight();
  const [inspectorHeight, setInspectorHeight] = useState(() => {
    // Persist the user's preferred height across refreshes.
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(INSPECTOR_HEIGHT_KEY) : null;
      const n = raw ? Number(raw) : NaN;
      if (Number.isFinite(n)) {
        return clampInspectorHeight(n);
      }
    } catch {
      // ignore (storage disabled / blocked)
    }
    return INSPECTOR_DEFAULT_HEIGHT;
  });
  const [inspectorActiveTab, setInspectorActiveTab] = useState<string>('code');
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(INSPECTOR_DEFAULT_HEIGHT);

  // Computed
  const selectedNode = useMemo(() => {
    // Pending node lives in local state, not in the pipelineNodes array from the parent
    if (selectedNodeId === '__pending__' && pendingNode) return pendingNode;
    const found = pipelineNodes.find((n) => n.stepId === selectedNodeId) ?? null;
    if (found) return found;
    if (selectedGroupTable && selectedNodeId?.startsWith('__target_')) {
      return {
        stepId: selectedNodeId,
        stepType: 'target_table' as const,
        title: selectedGroupTable.name,
        description: selectedGroupTable.error ? `Error: ${selectedGroupTable.error}` : 'Target table',
        status: (selectedGroupTable.status || 'pending') as PipelineNode['status'],
        inputRefs: ['__mapping_output__'],
        outputRef: selectedGroupTable.viewName,
        codeSnippet: selectedGroupTable.ddl,
      } as PipelineNode;
    }
    if (selectedSourceTable && selectedNodeId?.startsWith('__source_') && !selectedNodeId.startsWith('__source_group__')) {
      return {
        stepId: selectedNodeId,
        stepType: 'source' as const,
        title: selectedSourceTable.name,
        description: `${selectedSourceTable.rowCount.toLocaleString()} rows × ${selectedSourceTable.columnCount} cols`,
        status: 'completed' as PipelineNode['status'],
        inputRefs: [],
        outputRef: selectedSourceTable.name,
      } as PipelineNode;
    }
    return null;
  }, [pipelineNodes, selectedNodeId, selectedGroupTable, selectedSourceTable, pendingNode]);

  const selectedOperation = useMemo(() => {
    if (!selectedNodeId) return null;
    return operations.find((op) => op.operation_id === selectedNodeId) ?? null;
  }, [selectedNodeId, operations]);

  const inspectorOpen = selectedNode !== null && workspaceMode !== 'mapping';

  useEffect(() => {
    if (!inspectorOpen && inspectorFullscreen) {
      setInspectorFullscreen(false);
    }
  }, [inspectorOpen, inspectorFullscreen]);

  useEffect(
    () => () => {
      resetFrameVisibility();
    },
    [resetFrameVisibility],
  );

  // Auto-expand inspector for creation mode
  const CREATION_MODE_HEIGHT = getCreationModeInspectorHeight();
  useEffect(() => {
    if (pendingNode && selectedNodeId === '__pending__' && inspectorHeight < CREATION_MODE_HEIGHT) {
      setInspectorHeight(clampInspectorHeight(CREATION_MODE_HEIGHT));
    }
  }, [pendingNode, selectedNodeId]);

  // Auto-expand inspector for data tab — capped to available viewport (never force 480px+).
  const DATA_TAB_MIN_HEIGHT = getDataTabInspectorHeight();
  useEffect(() => {
    if (inspectorOpen && inspectorActiveTab === 'data' && inspectorHeight < DATA_TAB_MIN_HEIGHT) {
      setInspectorHeight(clampInspectorHeight(DATA_TAB_MIN_HEIGHT));
    }
  }, [inspectorActiveTab, inspectorOpen]);

  // Keep inspector within the viewport when the window is resized.
  useEffect(() => {
    const onResize = () => {
      setInspectorHeight((h) => clampInspectorHeight(h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Clamp persisted / legacy heights that exceed the visible workspace (e.g. old 480px data-tab floor).
  useEffect(() => {
    setInspectorHeight((h) => clampInspectorHeight(h));
  }, []);

  // Persist user preference. Note: may be overwritten by auto-expand logic above,
  // which is intentional for "data" and creation modes.
  useEffect(() => {
    try {
      window.localStorage.setItem(INSPECTOR_HEIGHT_KEY, String(inspectorHeight));
    } catch {
      // ignore
    }
  }, [inspectorHeight]);

  // Callbacks
  const handleNodeClick = useCallback((nodeId: string) => {
    if (nodeId === '__pipeline_output__') {
      onOpenOutputsDrawer();
      return;
    }
    if (nodeId === '__source_group__' || nodeId === '__destination__' || nodeId === '__target_group__') {
      return;
    }
    setSelectedGroupTable(null);
    setSelectedSourceTable(null);
    setSelectedNodeId((prev) => (prev === nodeId ? null : nodeId));
  }, [onOpenOutputsDrawer]);

  const exitInspectorFullscreen = useCallback(() => {
    setInspectorFullscreen(false);
  }, []);

  const toggleInspectorFullscreen = useCallback(() => {
    setInspectorFullscreen((prev) => !prev);
  }, []);

  const handleDeselectNode = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedGroupTable(null);
    setSelectedSourceTable(null);
    setInspectorFullscreen(false);
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedGroupTable(null);
    setSelectedSourceTable(null);
    setInspectorFullscreen(false);
  }, []);

  const canAddNode = useMemo((): CanAddNodeResult => {
    if (pendingNode) return { allowed: false, reason: 'Complete or cancel the current step first' };
    if (isBusy) return { allowed: false, reason: 'Wait for the agent to finish' };
    if (workspaceTables.length === 0) return { allowed: false, reason: 'Add source data first' };
    return { allowed: true };
  }, [pendingNode, isBusy, workspaceTables]);

  const handleAddNode = useCallback(
    (type: AddNodeType) => {
      if (!canAddNode.allowed) return;

      const existingNames = new Set(workspaceTables.map((t) => t.name));
      let n = 1;
      while (existingNames.has(`transform_${n}`)) n++;
      const outputName = `transform_${n}`;

      const def = getNodeTypeDef(type);
      const title = def ? `New ${def.label}` : `New ${type.replace(/_/g, ' ')}`;

      // Auto-connect: if a node is selected and has an outputRef, use it as input
      const autoInputRefs: string[] = [];
      let initialSql: string | undefined;
      if (selectedNodeId && selectedNodeId !== '__pending__') {
        const upstream = pipelineNodes.find((n) => n.stepId === selectedNodeId);
        if (upstream?.outputRef) {
          autoInputRefs.push(upstream.outputRef);
          if (type === 'sql_transform') {
            initialSql = `SELECT *\nFROM "${upstream.outputRef}"`;
          }
        }
      }

      const placeholder: PipelineNode = {
        stepId: '__pending__',
        stepType: type,
        title,
        description: 'Configuring...',
        status: 'pending',
        inputRefs: autoInputRefs,
        _pendingOutputName: outputName,
        _initialSql: initialSql,
      } as PipelineNode & { _pendingOutputName?: string; _initialSql?: string };

      setPendingNode(placeholder);
      setSelectedNodeId('__pending__');

      savePendingSnapshot(sessionId, {
        stepType: type,
        title,
        outputName,
        inputRefs: autoInputRefs,
        initialSql,
        creationCode: initialSql || '',
        creationExploratory: false,
      });
    },
    [workspaceTables, sessionId, canAddNode, selectedNodeId, pipelineNodes],
  );

  const handleAddTransform = useCallback(() => {
    const existingNames = new Set(workspaceTables.map((t) => t.name));
    let n = 1;
    while (existingNames.has(`post_transform_${n}`)) n++;
    const outputName = `post_transform_${n}`;

    const firstMapped = completedOutputs.find((o) => o.phase === 'post_mapping');
    const inputRef = firstMapped?.outputRef || '';
    const initialSql = inputRef ? `SELECT *\nFROM "${inputRef}"` : undefined;

    const placeholder: PipelineNode = {
      stepId: '__pending__',
      stepType: 'sql_transform',
      title: 'Post-Mapping SQL Transform',
      description: 'Configuring...',
      status: 'pending',
      inputRefs: inputRef ? [inputRef] : [],
      _pendingOutputName: outputName,
      _initialSql: initialSql,
    } as PipelineNode & { _pendingOutputName?: string; _initialSql?: string };

    setPendingNode(placeholder);
    setSelectedNodeId('__pending__');

    savePendingSnapshot(sessionId, {
      stepType: 'sql_transform',
      title: 'Post-Mapping SQL Transform',
      outputName,
      inputRefs: inputRef ? [inputRef] : [],
      initialSql,
      creationCode: initialSql || '',
      creationExploratory: false,
    });
  }, [workspaceTables, completedOutputs, sessionId]);

  const handleCancelPendingNode = useCallback(() => {
    setPendingNode(null);
    setSelectedNodeId(null);
    setInspectorFullscreen(false);
    clearPendingSnapshot(sessionId);
    setRestoredSnapshot(null);
  }, [sessionId]);

  const handlePendingNodeCreated = useCallback(() => {
    setPendingNode(null);
    setInspectorFullscreen(false);
    clearPendingSnapshot(sessionId);
    setRestoredSnapshot(null);
  }, [sessionId]);

  const handleInspectorDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startYRef.current = e.clientY;
      startHeightRef.current = inspectorHeight;

      const handleMove = (moveEvent: MouseEvent) => {
        if (!isDraggingRef.current) return;
        const delta = startYRef.current - moveEvent.clientY;
        setInspectorHeight(clampInspectorHeight(startHeightRef.current + delta));
      };

      const handleUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    },
    [inspectorHeight],
  );

  const handleAskAboutColumn = useCallback((tableName: string, columnName: string) => {
    const body = `Analyze the "${columnName}" column in the "${tableName}" table. What data quality issues or useful transformations do you see?`;
    const prefix = selectedNode
      ? `[Re: ${selectedNode.title || selectedNode.stepType}] `
      : '';
    setAskWorkspaceMessage(`${prefix}${body}`);
    setInspectorFullscreen(false);
  }, [selectedNode]);

  const handlePendingMessageConsumed = useCallback(() => {
    setAskWorkspaceMessage(null);
  }, []);

  const updatePendingCreationState = useCallback(
    (state: { code?: string; outputName?: string; inputRefs?: string[]; exploratory?: boolean }) => {
      const existing = loadPendingSnapshot(sessionId);
      if (!existing) return;
      savePendingSnapshot(sessionId, {
        ...existing,
        creationCode: state.code ?? existing.creationCode,
        outputName: state.outputName ?? existing.outputName,
        inputRefs: state.inputRefs ?? existing.inputRefs,
        creationExploratory: state.exploratory ?? existing.creationExploratory,
      });
    },
    [sessionId],
  );

  const handleAskAgentAboutNode = useCallback(
    (instruction: string) => {
      const node = selectedNode;
      const op = selectedOperation;
      const title = node?.title || node?.stepType || 'step';
      const prefix = `[Re: ${title}] `;

      const parts: string[] = [instruction];

      // Append context from the operation if available
      if (op) {
        const codeSnippet = op.config?.sql || op.config?.function_code || op.config?.code || '';
        if (codeSnippet) {
          const codePreview = codeSnippet.length > 500 ? codeSnippet.slice(0, 500) + '...' : codeSnippet;
          parts.push(`\nContext — ${op.operation_type.replace(/_/g, ' ')} transform:`);
          parts.push(`Code: ${codePreview}`);
        }
        if (op.input_refs?.length) parts.push(`Input tables: ${op.input_refs.join(', ')}`);
        if (op.output_ref) parts.push(`Output table: ${op.output_ref}`);
        if (op.error) parts.push(`Error: ${op.error}`);
      }

      setAskWorkspaceMessage(`${prefix}${parts.join('\n')}`);
      // Keep inspector open — chat sidebar is visible alongside
    },
    [selectedNode, selectedOperation],
  );

  return {
    selectedNodeId,
    selectedNode,
    selectedOperation,
    pendingNode,
    canAddNode,
    inspectorOpen,
    inspectorHeight,
    inspectorActiveTab,
    askWorkspaceMessage,
    inspectorFullscreen,
    restoredPendingSnapshot: restoredSnapshot,
    toggleInspectorFullscreen,
    exitInspectorFullscreen,
    setSelectedNodeId,
    setSelectedGroupTable,
    setSelectedSourceTable,
    setInspectorActiveTab,
    handleNodeClick,
    handleDeselectNode,
    handlePaneClick,
    handleAddNode,
    handleAddTransform,
    handleCancelPendingNode,
    handlePendingNodeCreated,
    handleInspectorDragStart,
    handleAskAboutColumn,
    handlePendingMessageConsumed,
    handleAskAgentAboutNode,
    updatePendingCreationState,
    INSPECTOR_COLLAPSED_HEIGHT: 36,
  };
}
